import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Sustainability and Global Responsibility',
    content: [
      '<strong>Learning Goal:</strong> Understand sustainability as a form of long-term well-being, learn how small lifestyle choices contribute to global ecological balance, and commit to personal or community environmental action.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Global environmental issues and their impact',
      '• Sustainable living principles',
      '• The connection between ecological and human health',
      '• Environmental stewardship as a moral and cultural value',
      '<br/>',
      '<strong>Discussion:</strong> Environmental well-being extends beyond personal comfort – it includes caring for the planet and promoting sustainable living. Your individual choices contribute to global ecological health, which in turn affects everyone\'s well-being.'
    ]
  },
  {
    title: 'Global Environmental Challenges',
    content: [
      'Understanding global environmental issues helps contextualize personal environmental well-being.',
      '<br/>',
      '<strong>Climate Change:</strong>',
      '• Rising global temperatures affecting weather patterns',
      '• Increased frequency of extreme weather events',
      '• Impact on agriculture, water availability, and human health',
      '• Displacement of populations and increased migration',
      '<br/>',
      '<strong>Air and Water Pollution:</strong>',
      '• Industrial emissions degrading air quality worldwide',
      '• Water contamination affecting drinking supplies',
      '• Ocean acidification harming marine ecosystems',
      '• Microplastics pollution in water systems',
      '<br/>',
      '<strong>Biodiversity Loss:</strong>',
      '• Species extinction at unprecedented rates',
      '• Disruption of ecosystems and food chains',
      '• Loss of natural resources and medicines',
      '• Reduced ecosystem services (pollination, water purification)',
      '<br/>',
      '<strong>Resource Depletion:</strong>',
      '• Overconsumption of finite resources',
      '• Deforestation and habitat destruction',
      '• Soil degradation and desertification',
      '• Peak resource extraction challenges',
      '<br/>',
      '<strong>The Human Cost:</strong> These environmental issues disproportionately affect vulnerable populations, creating cycles of poverty, health problems, and social instability.'
    ]
  },
  {
    title: 'Sustainable Living Principles',
    content: [
      'Sustainable living involves making choices that meet current needs without compromising future generations.',
      '<br/>',
      '<strong>The Three R\'s:</strong>',
      '• <strong>Reduce:</strong> Minimize consumption and waste generation',
      '• <strong>Reuse:</strong> Extend the life of products through reuse',
      '• <strong>Recycle:</strong> Convert waste into new materials',
      '<br/>',
      '<strong>Additional Principles:</strong>',
      '• <strong>Rethink:</strong> Question consumption patterns and needs',
      '• <strong>Refuse:</strong> Decline unnecessary items and packaging',
      '• <strong>Repair:</strong> Fix rather than replace items',
      '• <strong>Regenerate:</strong> Restore and improve natural systems',
      '<br/>',
      '<strong>Practical Applications:</strong>',
      '• <strong>Energy:</strong> Use renewable sources, improve efficiency, reduce usage',
      '• <strong>Transportation:</strong> Choose public transit, biking, walking, or electric vehicles',
      '• <strong>Food:</strong> Eat locally, reduce meat consumption, minimize food waste',
      '• <strong>Water:</strong> Conserve water, fix leaks, use water-efficient appliances',
      '• <strong>Shopping:</strong> Buy durable, repairable items; choose sustainable brands',
      '<br/>',
      '<strong>The Mindset Shift:</strong> Sustainability isn\'t about deprivation – it\'s about conscious consumption that enhances quality of life while protecting the planet.'
    ]
  },
  {
    title: 'Ecological Health and Human Well-Being',
    content: [
      'The health of ecosystems directly impacts human physical and mental well-being.',
      '<br/>',
      '<strong>Ecosystem Services:</strong>',
      '• <strong>Clean Air:</strong> Forests and oceans filter pollutants',
      '• <strong>Clean Water:</strong> Natural systems purify and regulate water',
      '• <strong>Food Production:</strong> Soil health and pollination support agriculture',
      '• <strong>Climate Regulation:</strong> Oceans and forests stabilize temperature',
      '• <strong>Disease Control:</strong> Biodiversity reduces disease transmission risk',
      '<br/>',
      '<strong>Mental Health Connections:</strong>',
      '• <strong>Nature Access:</strong> Green spaces reduce stress and improve mood',
      '• <strong>Climate Anxiety:</strong> Environmental concerns affect mental health',
      '• <strong>Environmental Justice:</strong> Unequal exposure to pollution affects communities',
      '• <strong>Intergenerational Trauma:</strong> Climate impacts affect future generations',
      '<br/>',
      '<strong>One Health Approach:</strong> Human health is interconnected with animal and environmental health. Protecting ecosystems protects human health.',
      '<br/>',
      '<strong>Research Evidence:</strong> Studies show that sustainable communities have:',
      '• 20-30% lower rates of chronic diseases',
      '• Better mental health outcomes',
      '• Stronger social cohesion',
      '• Higher life satisfaction scores',
      '<br/>',
      '<strong>The Ripple Effect:</strong> Individual sustainable choices create community benefits, which build regional improvements, leading to global positive change.'
    ]
  },
  {
    title: 'Environmental Stewardship',
    content: [
      'Environmental stewardship involves taking responsibility for protecting and improving the natural world.',
      '<br/>',
      '<strong>Moral and Ethical Dimensions:</strong>',
      '• <strong>Intergenerational Justice:</strong> Protecting resources for future generations',
      '• <strong>Intrinsic Value:</strong> Recognizing nature\'s inherent worth beyond human use',
      '• <strong>Compassion:</strong> Caring for all living beings and ecosystems',
      '• <strong>Humility:</strong> Acknowledging human dependence on natural systems',
      '<br/>',
      '<strong>Cultural Perspectives:</strong>',
      '• <strong>Indigenous Wisdom:</strong> Traditional ecological knowledge and land stewardship',
      '• <strong>Spiritual Connections:</strong> Many cultures view nature as sacred',
      '• <strong>Community Values:</strong> Environmental care as a shared cultural responsibility',
      '• <strong>Educational Role:</strong> Teaching environmental stewardship to future generations',
      '<br/>',
      '<strong>Practical Stewardship Actions:</strong>',
      '• <strong>Conservation:</strong> Protecting natural habitats and biodiversity',
      '• <strong>Restoration:</strong> Rehabilitating degraded ecosystems',
      '• <strong>Advocacy:</strong> Supporting environmental policies and regulations',
      '• <strong>Education:</strong> Raising awareness and building knowledge',
      '• <strong>Collaboration:</strong> Working with communities and organizations',
      '<br/>',
      '<strong>Personal Impact:</strong> Individual stewardship actions, when multiplied across communities, create significant environmental improvements and enhance collective well-being.'
    ]
  },
  {
    title: 'Sustainability Pledge Activity',
    content: [
      'Create a personal sustainability pledge and commit to environmental action.',
      '<br/>',
      '<strong>Activity Steps:</strong>',
      '1. <strong>Reflect on Impact:</strong> Consider how your daily choices affect the environment',
      '2. <strong>Identify Priorities:</strong> Choose 3 areas where you can make sustainable changes',
      '3. <strong>Write Specific Pledges:</strong> Create clear, achievable commitments',
      '4. <strong>Share with Others:</strong> Discuss your pledges in pairs or groups for accountability',
      '5. <strong>Track Progress:</strong> Monitor your actions and celebrate successes',
      '<br/>',
      '<strong>Pledge Categories:</strong>',
      '<br/>',
      '<strong>Energy Use:</strong>',
      '• "I will unplug electronics when not in use and use LED bulbs throughout my home"',
      '• "I will adjust my thermostat by 2-3 degrees to reduce energy consumption"',
      '• "I will choose public transportation or biking for trips under 2 miles"',
      '<br/>',
      '<strong>Waste Reduction:</strong>',
      '• "I will use reusable bags, bottles, and containers instead of disposables"',
      '• "I will compost food waste and recycle properly"',
      '• "I will buy products with minimal or recyclable packaging"',
      '<br/>',
      '<strong>Consumption Habits:</strong>',
      '• "I will eat one plant-based meal per day to reduce my environmental footprint"',
      '• "I will repair items instead of replacing them when possible"',
      '• "I will buy second-hand or sustainably-made products"',
      '<br/>',
      '<strong>Transportation:</strong>',
      '• "I will combine errands to reduce driving and carpool when possible"',
      '• "I will maintain proper tire pressure and vehicle maintenance for efficiency"',
      '• "I will choose fuel-efficient routes and avoid idling"',
      '<br/>',
      '<strong>Sample Pledge:</strong>',
      '"I pledge to reduce my energy use by unplugging devices, using LED bulbs, and adjusting my thermostat. I will also reduce waste by using reusable containers and composting. Finally, I will choose sustainable transportation options for short trips."',
      '<br/>',
      '<strong>Accountability:</strong> Share your pledge with a friend, family member, or online community. Regular check-ins help maintain commitment and create positive social reinforcement.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 5: Sustainability and Global Responsibility! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about sustainability and environmental stewardship.',
        'The quiz will help you review key concepts about global environmental challenges and sustainable living.'
      ]
    }
  }
];

const EnvironmentalModule5Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('environmentalM5');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/environmental-resources');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/environmental-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left environmental-panel"
        >
          <div className="module-header">
            <h1>Module 5</h1>
            <p className="module-subtitle">Sustainability and Global Responsibility</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Explore how environmental well-being connects to planetary health and sustainability.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/environmental-module-5/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalModule5Page;

